import type { PersonProfilePayload, ProfileRelationshipMember } from './profileTypes'
import type { CastProfileTimeline } from './castProfileTimelines'

export type RelationshipStatusTone = 'positive' | 'caution' | 'ended' | 'unverified'

export type WhereItStarted = {
  relationshipId: string
  introLabel?: string
  partner: ProfileRelationshipMember
  show: string | null
  season: number | null
  statusKey: string
  statusLabel: string
  statusTone: RelationshipStatusTone
  statusAsOf: string | null
}

// Attribute the exact reviewed original arc, not a later partner or a
// family/friend connection. Unreviewed statuses have no published citation.
export function publishedOriginStatusSource(record: PersonProfilePayload | null, editorialTimeline?: CastProfileTimeline) {
  const origin = whereItStartedForProfile(record, editorialTimeline)
  if (!origin?.relationshipId || origin.statusKey === 'unverified') return null
  const arc = record?.relationships.find(item => item.id === origin.relationshipId)
  if (arc?.status_review !== 'published' || !arc.source_url) return null
  try {
    const url = new URL(arc.source_url)
    if (url.protocol !== 'https:') return null
    return { url: url.toString(), asOf: arc.status_as_of }
  } catch { return null }
}

const statusPresentations: Record<string, { label: string; tone: RelationshipStatusTone }> = {
  together: { label: 'Together', tone: 'positive' },
  engaged: { label: 'Engaged', tone: 'positive' },
  married: { label: 'Married', tone: 'positive' },
  separated: { label: 'Separated', tone: 'caution' },
  divorce_pending: { label: 'Divorce pending', tone: 'caution' },
  divorced: { label: 'Divorced', tone: 'ended' },
  split: { label: 'Split', tone: 'ended' },
}

function normalizeName(value: string | null) {
  return value
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .toLocaleLowerCase() ?? ''
}

function namesReferToEachOther(left: string, right: string) {
  if (!left || !right) return false
  const leftTokens = left.split(' ')
  const rightTokens = right.split(' ')
  return leftTokens.every((token) => rightTokens.includes(token))
    || rightTokens.every((token) => leftTokens.includes(token))
}

export function relationshipStatus(status: string | null, review: string) {
  if (review.trim().toLocaleLowerCase() !== 'published') {
    return { key: 'unverified', label: 'Being verified', tone: 'unverified' as const }
  }

  const normalized = status
    ?.trim()
    .toLocaleLowerCase()
    .replace(/[\s-]+/g, '_') ?? ''
  if (normalized.startsWith('relationship_ended')) {
    return { key: 'split', ...statusPresentations.split }
  }
  const presentation = statusPresentations[normalized]
  return presentation
    ? { key: normalized, ...presentation }
    : { key: 'unverified', label: 'Being verified', tone: 'unverified' as const }
}

export function whereItStartedForProfile(record: PersonProfilePayload | null, editorialTimeline?: CastProfileTimeline): WhereItStarted | null {
  if (!record) return null

  const editorialOrigin = editorialTimeline?.origin
  if (editorialOrigin) {
    const arc = editorialOrigin.connectionKind === 'family' || editorialOrigin.connectionKind === 'friend' || !editorialOrigin.partnerPersonId ? undefined
      : record.relationships.find((candidate) => candidate.members.length === 2
        && candidate.members.some((member) => member.person_id === editorialOrigin.partnerPersonId))
    const status = arc ? relationshipStatus(arc.status, arc.status_review)
      : { key: 'unverified', label: editorialOrigin.connectionKind === 'family' ? 'Family connection' : editorialOrigin.connectionKind === 'friend' ? 'Friend connection' : editorialOrigin.connectionKind === 'unverified-identity' ? 'Identity unverified' : editorialOrigin.connectionKind === 'independent' ? 'No confirmed match' : 'Being verified', tone: 'unverified' as const }
    return {
      relationshipId: arc?.id ?? '', introLabel: editorialOrigin.introLabel,
      partner: { person_id: editorialOrigin.partnerPersonId ?? '', name: editorialOrigin.partnerName, full_name: editorialOrigin.partnerName, credited_name: editorialOrigin.partnerName, position: 1 },
      show: editorialOrigin.show, season: editorialOrigin.season,
      statusKey: status.key, statusLabel: status.label, statusTone: status.tone,
      statusAsOf: arc?.status_review === 'published' ? arc.status_as_of : null,
    }
  }

  // ET's direct interview/recap distinguishes the impersonated online persona
  // from the woman whose pictures were used (sources in castBios2021B).
  if (record.person.id === 'p-tyray-mollett') return {
    relationshipId: '', introLabel: 'Online identity: “Carmella”',
    partner: { person_id: '', name: '“Carmella” online identity', full_name: null, credited_name: 'Carmella', position: 1 },
    show: record.person.first_show, season: record.person.first_season,
    statusKey: 'unverified', statusLabel: 'Impersonated identity', statusTone: 'unverified', statusAsOf: null,
  }

  const firstPartner = normalizeName(record.person.first_partner)
  const candidates = record.relationships.flatMap((relationship, index) => {
    const partners = relationship.members.filter((member) => member.person_id !== record.person.id)
    if (partners.length !== 1) return []
    const partner = partners[0]
    const partnerNames = [partner.name, partner.full_name, partner.credited_name].map(normalizeName)
    const partnerMatch = partnerNames.some((name) => namesReferToEachOther(firstPartner, name))
    const showMatch = relationship.first_show === record.person.first_show
    const seasonMatch = relationship.first_season === record.person.first_season
    const status = relationshipStatus(relationship.status, relationship.status_review)

    return [{
      relationshipId: relationship.id,
      partner,
      show: relationship.first_show ?? record.person.first_show,
      season: relationship.first_season ?? record.person.first_season,
      statusKey: status.key,
      statusLabel: status.label,
      statusTone: status.tone,
      statusAsOf: relationship.status_as_of,
      score: (partnerMatch ? 100 : 0) + (showMatch ? 20 : 0) + (seasonMatch ? 10 : 0),
      firstYear: relationship.first_year ?? Number.MAX_SAFE_INTEGER,
      index,
    }]
  })

  const catalogJourney = candidates.sort((left, right) => {
    if (left.score !== right.score) return right.score - left.score
    if (left.firstYear !== right.firstYear) return left.firstYear - right.firstYear
    return left.index - right.index
  }).map(({ score: _score, firstYear: _firstYear, index: _index, ...journey }) => journey)[0]
  if (catalogJourney) {
    const announcedDebut = editorialTimeline?.seasons.some((chapter) => chapter.show === record.person.first_show
      && chapter.season === record.person.first_season && chapter.status === 'announced')
    return announcedDebut ? { ...catalogJourney, introLabel: `Announced with ${catalogJourney.partner.name}` } : catalogJourney
  }

  // Some verified debut couples have linked editorial profiles but no D1 arc.
  // Only match the named debut partner; a later partner or family connection
  // must not silently become the original relationship. No status is inferred.
  if (!firstPartner) return null
  const matchingConnections = editorialTimeline?.connections.filter((connection) => connection.personId
    && namesReferToEachOther(firstPartner, normalizeName(connection.name))) ?? []
  if (matchingConnections.length !== 1) return null
  const connection = matchingConnections[0]
  return {
    relationshipId: connection.relationshipId ?? '',
    partner: { person_id: connection.personId!, name: connection.name, full_name: connection.name, credited_name: connection.name, position: 1 },
    show: record.person.first_show,
    season: record.person.first_season,
    statusKey: 'unverified', statusLabel: 'Being verified', statusTone: 'unverified', statusAsOf: null,
  }
}
